<template>
  <div
    class="rr-input rr-input_default"
    :class="classes"
  >
    <!-- Left icons : BEGIN -->
    <div class="rr-input__icons rr-input__icons_left">
      <div
        v-if="iconLeft"
        class="rr-input__icon"
      >
        <Icon :name="iconLeft"></Icon>
      </div>
      <slot name="icons-left"></slot>
    </div>
    <!-- Left icons : END -->
    <!-- Input : BEGIN -->
    <input
      class="rr-input__el"
      v-model="internalValue"
      :id="id"
      :type="inputType"
      :placeholder="placeholder"
      :name="name"
      :autocomplete="autocomplete"
      :disabled="disabled || loading"
      :readonly="readonly"
      @change="$emit('change', $event)"
      @keydown="$emit('keydown', $event)"
      @click="$emit('click', $event)"
      @blur="$emit('blur', $event)"
    >
    <!-- Input : END -->
    <!-- Right icons : BEGIN -->
    <div class="rr-input__icons rr-input__icons_right">
      <!-- State icons : BEGIN -->
      <template v-if="!noStateIcons">
        <div
          class="rr-input__icon"
          v-for="(icon, state) in stateIcons"
          :key="state"
          :class="`rr-input__icon-${state}`"
        >
          <Icon :name="icon"></Icon>
        </div>
      </template>
      <!-- State icons : END -->
      <!-- Password switch : BEGIN -->
      <button
        v-if="passwordSwitch"
        class="rr-input__icon rr-input__icon_hover"
        type="button"
        @click.prevent="togglePassword"
      >
        <Icon :name="passwordVisible ? 'eye' : 'eye-invisible'"></Icon>
      </button>
      <!-- Password switch : END -->
      <!-- Clear button : BEGIN -->
      <button
        v-if="clearable"
        v-show="showClearButton"
        class="rr-input__icon rr-input__icon_hover"
        type="button"
        @click.stop="internalValue = null"
      >
        <Icon name="close-circle-s"></Icon>
      </button>
      <!-- Clear button : END -->
      <!-- Spinner : BEGIN -->
      <div
        v-if="loading"
        class="rr-input__icon rr-input__icon-spinner"
      >
      </div>
      <!-- Spinner : END -->
      <slot name="icons-right"></slot>
      <div
        v-if="iconRight"
        class="rr-input__icon"
      >
        <Icon :name="iconRight"></Icon>
      </div>
    </div>
    <!-- Right icons : END -->
    <!-- Border : BEGIN -->
    <div class="rr-input__border" v-if="!noBorder"></div>
    <!-- Border : END -->
  </div>
</template>

<script>
import Icon from '@rr-component-library/icon/src/main';

export default {
  name: 'Input',
  designVersion: '2.0.0',
  components: {
    Icon,
  },
  model: {
    prop: 'inputModel',
  },
  props: {
    inputModel: {
      default: '',
    },
    /**
     * HTML-атрибут type для инпута
     */
    type: {
      type: String,
      default: 'text',
    },
    /**
     * Состояние
     */
    state: {
      type: String,
      validator(value) {
        return ['', 'error', 'warning', 'success'].indexOf(value) !== -1;
      },
    },
    /**
     * Размер
     */
    size: {
      type: String,
      default: 'md',
      validator(value) {
        return ['', 'sm', 'md'].indexOf(value) !== -1;
      },
    },
    /**
     * HTML-атрибут placeholder для инпута
     */
    placeholder: String,
    /**
     *  Идентификатор \<input\> в DOM
     */
    id: String,
    /**
     *  Имя \<input\> в DOM
     */
    name: String,
    /**
     *  HTML-атрибут autocomplete для инпута
     */
    autocomplete: String,
    /**
     * Добавляет крестик для удаления значения инпута
     */
    clearable: Boolean,
    /**
     *  Делает инпут недоступным
     */
    disabled: Boolean,
    /**
     *  Делает инпут доступным только для чтения
     */
    readonly: Boolean,
    /**
     *  Состояние загрузки.<br>
     *  Добавляет иконку прелоадера
     */
    loading: Boolean,
    /**
     *  Убирает бордер вокруг инпута
     */
    noBorder: Boolean,
    /**
     *  Скрывает иконки состояния
     */
    noStateIcons: Boolean,
    /**
     *  Иконка слева
     */
    iconLeft: String,
    /**
     *  Иконка справа
     */
    iconRight: String,
    /**
     *  Добавляет переключатель для показа/скрытия пароля
     */
    passwordSwitch: Boolean,
    /**
     *  Увеличенный размер на мобильных устройствах
     */
    mobile: Boolean,
  },
  data() {
    return {
      passwordVisible: false,
      inputType: this.type,
      stateIcons: {
        error: 'exclamation-circle-s',
        warning: 'warning-s',
        success: 'check-bold',
      },
    };
  },
  computed: {
    internalValue: {
      get() {
        return this.inputModel;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    classes() {
      return {
        'rr-input_md': this.size === 'md',
        'rr-input_sm': this.size === 'sm',
        'has-error': this.state === 'error',
        'has-warning': this.state === 'warning',
        'has-success': this.state === 'success',
        'rr-input_mobile': this.mobile,
      };
    },
    showClearButton() {
      return this.internalValue && !this.disabled && !this.loading && !this.readonly;
    },
  },
  methods: {
    togglePassword() {
      this.passwordVisible = !this.passwordVisible;
      this.inputType = this.passwordVisible ? 'text' : 'password';
    },
  },
};
</script>

<style lang="scss" scoped>
  @import "./Input";
</style>
